import React, { useState, useCallback } from 'react';
import { useCookies } from 'react-cookie';
import { observer } from 'mobx-react-lite';
import debounce from 'lodash.debounce';

import { useMap } from '../Map';

import { SidebarPanel } from '../components/Sidebar/SidebarPanel';
import { Searchmask } from '../components/Search/Searchmask';
import { SearchItem } from '../components/Search/SearchItem';
import { SearchHistoryItem } from '../components/Search/SearchHistoryItem';

import { useStore } from '../stores';
import { Layers, Modals } from '../stores/layers';
import { DistanceTypes } from '../stores/user';
import { Distances } from '../stores/search';

import { flyToPoi } from '../helpers/mapHelper';
import { getDistanceString } from '../helpers/textUtils';
import { higestRankedCategory } from '../helpers/poiHelper';

import { searchHistory as searchHistoryCookieName } from '../helpers/cookies';
import { useTranslateFunction } from '../helpers/useTranslationHook';

import classNames from 'classnames';

export const Search = observer(() => {
  const { t } = useTranslateFunction(Layers.search);
  const { t: categoryTranslation } = useTranslateFunction(Layers.filter);

  const { search, layers, user, poi, config } = useStore();

  const [searchText, setSearchText] = useState('');
  const [cookies, setCookie] = useCookies([searchHistoryCookieName]);
  const [searchHistory, setSearchHistory] = useState(
    cookies.searchHistory || [],
  );

  const map = useMap();

  const startSearch = (text) => {
    let newSearchHistory = searchHistory;
    newSearchHistory.push({ text });

    newSearchHistory = newSearchHistory.slice(-3);

    setSearchHistory(newSearchHistory);
    setCookie(searchHistoryCookieName, newSearchHistory, {
      path: '/',
    });

    if (user.position?.latitude && user.position?.longitude) {
      search.fetchSearchResults(text, user.position);
    } else search.fetchSearchResults(text, config.initialPosition);
  };

  const debouncedSearch = useCallback(
    debounce(startSearch, config.gysService.debounceDelay),
    [],
  );

  const setSearchRadius = (radius) => {
    if (!user.position) layers.openModal(Modals.noLocation);
    else {
      if (search.searchRadius === radius) {
        search.setSearchRadius(Distances.infinite);
      } else {
        search.setSearchRadius(radius);
      }
      startSearch(searchText);
    }
  };

  return (
    <SidebarPanel
      isOpen={layers.activeLayer === Layers.search}
      title={t('headline')}
      closeAction={layers.closeLayer}
    >
      <div className="h-full p-5 overflow-auto">
        <Searchmask
          onChange={(value) => {
            setSearchText(value.target.value);
            debouncedSearch(value.target.value);
            search.setLoadingStatus();
          }}
          onClear={() => {
            setSearchText('');
            search.clearSearchResults();
          }}
          setText={setSearchText}
          showClearButton={searchText.length && !search.loading}
          loading={search.loading}
          text={searchText}
        />

        <div className="my-3">
          <button
            className={classNames(
              'border border-neutral-300 py-1 px-2 mr-2 text-xs rounded-full hover:bg-neutral-100 transition-all',
              {
                'bg-neutral-200': search.searchRadius === Distances.small,
                'bg-white': search.searchRadius !== Distances.small,
              },
            )}
            onClick={debounce(
              () => setSearchRadius(Distances.small),
              config.gysService.debounceDelay,
            )}
          >
            {t('distance_small')}
          </button>

          <button
            className={classNames(
              'border border-neutral-300 py-1 px-2 mr-2 text-xs rounded-full hover:bg-neutral-100 transition-all',
              {
                'bg-neutral-200': search.searchRadius === Distances.large,
                'bg-white': search.searchRadius !== Distances.large,
              },
            )}
            onClick={debounce(
              () => setSearchRadius(Distances.large),
              config.gysService.debounceDelay,
            )}
          >
            {t('distance_large')}
          </button>
        </div>

        <div>
          {search?.searchResults?.map((result) => (
            <SearchItem
              title={result.poi[config.gysService.language]?.title || ''}
              text={[
                result.poi[config.gysService.language]?.description?.substring(
                  0,
                  40,
                ),
                result?.poi[config.gysService.language]?.description?.length
                  ? '...'
                  : '',
              ].join('')}
              distance={
                user.position
                  ? getDistanceString(
                      result.distance,
                      DistanceTypes[user.config.preferedDistanceSystem],
                    )
                  : null
              }
              icon={'mapdot'}
              category={higestRankedCategory(result?.poi)}
              categoryName={categoryTranslation(
                higestRankedCategory(result?.poi)?.name || '',
              )}
              onClick={() => {
                flyToPoi(map, result.poi, poi);
                layers.closeLayer();
              }}
            />
          ))}

          {!searchText.length
            ? search.recommendations.map(({ title, text, icon }) => (
                <SearchItem title={title} text={text} icon={icon} />
              ))
            : null}

          {!searchText.length
            ? searchHistory?.map(({ title, text }, index) => (
                <SearchHistoryItem
                  title={title}
                  text={text}
                  icon={'clock'}
                  onClick={() => {
                    setSearchText(text);
                    startSearch(text);
                  }}
                  key={index}
                />
              ))
            : null}

          {searchText.length &&
          !search?.searchResults?.length &&
          !search.loading ? (
            <div className="w-full min-h-[250px] flex justify-center items-center flex-col">
              <div className="mb-5 relative">
                <img
                  className="w-20 h-auto absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2"
                  src="./noresult-magnifier.svg"
                  alt=""
                />
                <img src="./noresult-bg.svg" alt="" />
              </div>
              {t('no_search_results')}
            </div>
          ) : null}
        </div>
      </div>
    </SidebarPanel>
  );
});
